/**
 * TradingView Datafeed Adapter
 *
 * This adapter implements the TradingView Datafeed API to connect
 * our existing chart data API to TradingView Advanced Charts.
 *
 * Data flow:
 * 1. TradingView calls getBars() with symbol (dataId) and time range
 * 2. We fetch from /api/chart-data/${dataId}
 * 3. Transform our format to TradingView Bar format
 * 4. Return bars to TradingView via callback
 *
 * Supported resolutions: 1D (daily), 1W (weekly), 1M (monthly)
 * TradingView automatically aggregates daily data to weekly/monthly.
 */

import {
  IDatafeedChartApi,
  IExternalDatafeed,
  LibrarySymbolInfo,
  ResolutionString,
  Bar,
  OnReadyCallback,
  SearchSymbolsCallback,
  ResolveCallback,
  DatafeedErrorCallback,
  HistoryCallback,
  PeriodParams,
  SubscribeBarsCallback,
} from '../../public/charting_library/datafeed-api';

import { fetchChartData, ChartDataPoint } from './chart-utils';
import { logger } from './logger';

/**
 * Custom datafeed implementation for TradingView Advanced Charts
 */
export class TradingViewDatafeed implements IDatafeedChartApi, IExternalDatafeed {

  private dataId: string;
  private ticker: string | null = null;
  private description: string | null = null;
  private sessionId: string | null = null;
  private userId: string | null = null;
  private cachedBars: Map<string, Bar[]> = new Map();
  private earliestBarTime: Map<string, number> = new Map();
  private latestBarTime: Map<string, number> = new Map();
  private symbolMap: Map<string, { dataId: string; ticker: string }> = new Map();

  /**
   * @param dataId - The chart data identifier used to fetch data from API
   * @param ticker - Optional ticker symbol for display (e.g., "AAPL", "BTC-USD")
   * @param sessionId - Optional session ID for backend API calls
   * @param userId - Optional user ID for backend API calls
   * @param comparisonDataIds - Optional array of datapack IDs for comparison charts
   * @param comparisonTickers - Optional array of ticker names for comparison charts
   * @param prefetchedDatapacks - Optional Map of dataId -> data for historical sessions
   */
  constructor(
    dataId: string,
    ticker?: string,
    sessionId?: string,
    userId?: string,
    comparisonDataIds?: string[],
    comparisonTickers?: string[],
    prefetchedDatapacks?: Map<string, ChartDataPoint[]>
  ) {
    this.dataId = dataId;
    if (ticker) {
      this.ticker = ticker;
    }
    if (sessionId) {
      this.sessionId = sessionId;
    }
    if (userId) {
      this.userId = userId;
    }

    // Build symbol map for multi-symbol support (comparison charts and single-asset charts)
    // Main symbol - register by dataId, ticker, and friendly display name
    const mainTicker = ticker || dataId;
    const mainDisplayName = `${mainTicker} Historical Prices`;

    // Always register all three: dataId, ticker, and display name
    this.symbolMap.set(dataId, { dataId, ticker: mainTicker });
    this.symbolMap.set(mainTicker, { dataId, ticker: mainTicker });
    this.symbolMap.set(mainDisplayName, { dataId, ticker: mainTicker });

    // Comparison symbols - register by dataId, ticker, and friendly display name
    if (comparisonDataIds && comparisonTickers) {
      for (let i = 0; i < comparisonDataIds.length; i++) {
        const id = comparisonDataIds[i];
        const tick = comparisonTickers[i] || id;
        const displayName = `${tick} Historical Prices`;

        // Register by datapackId
        this.symbolMap.set(id, { dataId: id, ticker: tick });
        // Register by ticker
        this.symbolMap.set(tick, { dataId: id, ticker: tick });
        // Register by friendly display name (used for Compare study labels)
        this.symbolMap.set(displayName, { dataId: id, ticker: tick });

        logger.info(`[TradingViewDatafeed] Registered comparison symbol: "${id}", "${tick}", and "${displayName}"`);
      }
    }

    // Cache prefetched datapacks (for historical sessions)
    logger.info(`[TradingViewDatafeed] Constructor received prefetchedDatapacks:`, prefetchedDatapacks);
    logger.info(`[TradingViewDatafeed] Is Map:`, prefetchedDatapacks instanceof Map);
    logger.info(`[TradingViewDatafeed] Size:`, prefetchedDatapacks?.size);

    if (prefetchedDatapacks && prefetchedDatapacks.size > 0) {
      logger.info(`[TradingViewDatafeed] ✅ Caching ${prefetchedDatapacks.size} prefetched datapacks`);

      prefetchedDatapacks.forEach((rawData, datapackId) => {
        const bars = this.transformToBars(rawData);
        this.cachedBars.set(datapackId, bars);

        if (bars.length > 0) {
          this.earliestBarTime.set(datapackId, bars[0].time);
          this.latestBarTime.set(datapackId, bars[bars.length - 1].time);
          logger.info(`[TradingViewDatafeed] Cached ${bars.length} bars for datapack ${datapackId}`);
        }
      });
    }

    logger.info(`[TradingViewDatafeed] Initialized with ${this.symbolMap.size} symbol entries`);
  }

  /**
   * Configuration callback - defines datafeed capabilities
   * Called once when the chart is initialized
   */
  onReady(callback: OnReadyCallback): void {
    logger.info('[TradingViewDatafeed] onReady called');

    setTimeout(() => {
      const config = {
        // Only support daily, weekly, and monthly resolutions
        supported_resolutions: ['1D', '1W', '1M'] as ResolutionString[],

        // Optional: List of exchanges (we don't use this)
        exchanges: [],

        // Optional: List of symbol types (we don't use this)
        symbols_types: [],

        // We don't provide server time
        supports_time: false,

        // We don't provide marks on bars
        supports_marks: false,

        // We don't provide marks on timescale
        supports_timescale_marks: false,
      };

      logger.info('[TradingViewDatafeed] Returning config:', config);
      callback(config);
    }, 0);
  }

  /**
   * Symbol search - not needed for our use case
   * We have a single symbol per chart (dataId)
   */
  searchSymbols(
    userInput: string,
    exchange: string,
    symbolType: string,
    onResult: SearchSymbolsCallback
  ): void {
    logger.info('[TradingViewDatafeed] searchSymbols called - returning empty');
    onResult([]);
  }

  /**
   * Resolve symbol metadata
   * Returns information about the symbol (dataId) structure and capabilities
   */
  async resolveSymbol(
    symbolName: string,
    onResolve: ResolveCallback,
    onError: DatafeedErrorCallback
  ): Promise<void> {
    logger.info(`[TradingViewDatafeed] resolveSymbol: ${symbolName}`);

    try {
      // Check if symbol exists in our map
      const symbolData = this.symbolMap.get(symbolName);

      if (!symbolData) {
        logger.error(`[TradingViewDatafeed] Symbol not found: ${symbolName}`);
        onError(`Symbol not found: ${symbolName}`);
        return;
      }

      // Get earliest bar time if data is already cached
      const bars = this.cachedBars.get(symbolData.dataId);
      const earliestTime = bars && bars.length > 0 ? Math.floor(bars[0].time / 1000) : undefined;

      const symbolInfo: LibrarySymbolInfo = {
        // Symbol identification
        ticker: symbolData.ticker,
        name: symbolName, // Use symbolName (dataId) as the name for lookups
        description: `${symbolData.ticker} Historical Prices`,
        type: 'stock',

        // Session and timezone
        session: '24x7', // 24/7 trading
        timezone: 'Etc/UTC',

        // Exchange info
        exchange: 'Custom',
        listed_exchange: 'Custom',

        // Price format
        format: 'price',
        pricescale: 100, // 2 decimal places
        minmov: 1,

        // Resolution support
        has_intraday: false, // No intraday (1m, 5m, 1h)
        has_daily: true, // Has daily data
        has_weekly_and_monthly: true, // TradingView will build from daily

        supported_resolutions: ['1D', '1W', '1M'] as ResolutionString[],

        // Data boundaries - tell TradingView not to request data outside these bounds
        // This prevents whitespace from failed data requests
        ...(earliestTime && { data_status: 'streaming' as const }), // Mark as having limited data
      };

      logger.info(`[TradingViewDatafeed] Returning symbolInfo for ${symbolName}:`, symbolInfo);
      onResolve(symbolInfo);
    } catch (error) {
      logger.error('[TradingViewDatafeed] Error in resolveSymbol:', error);
      onError(error instanceof Error ? error.message : 'Failed to resolve symbol');
    }
  }

  /**
   * Get historical bars
   * This is the most critical method - fetches and returns bar data
   *
   * @param symbolInfo - Symbol metadata from resolveSymbol
   * @param resolution - Requested resolution (1D, 1W, or 1M)
   * @param periodParams - Time range parameters
   * @param onResult - Callback to return bars
   * @param onError - Callback for errors
   */
  async getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback
  ): Promise<void> {
    logger.info('[TradingViewDatafeed] getBars called', {
      symbol: symbolInfo.name,
      resolution,
      from: new Date(periodParams.from * 1000).toISOString(),
      to: new Date(periodParams.to * 1000).toISOString(),
      firstDataRequest: periodParams.firstDataRequest,
    });

    try {
      // Determine which datapack to fetch based on symbol name
      const symbolName = symbolInfo.name;
      const symbolData = this.symbolMap.get(symbolName);

      if (!symbolData) {
        logger.error(`[TradingViewDatafeed] Symbol not found in map: ${symbolName}`);
        onError(`Symbol not found: ${symbolName}`);
        return;
      }

      const datapackId = symbolData.dataId;

      // Check if data is already cached (from prefetch or previous fetch)
      logger.info(`[TradingViewDatafeed] getBars() called for datapackId: ${datapackId}`);
      logger.info(`[TradingViewDatafeed] Cache status: ${this.cachedBars.has(datapackId) ? 'HIT' : 'MISS'}`);
      logger.info(`[TradingViewDatafeed] Total cached entries: ${this.cachedBars.size}`);
      logger.info(`[TradingViewDatafeed] Cached datapack IDs:`, Array.from(this.cachedBars.keys()));

      // Fetch and cache data on first request for this datapack
      // Use datapackId as cache key (not symbolName) to avoid duplicate entries
      if (!this.cachedBars.has(datapackId)) {
        logger.info(`[TradingViewDatafeed] ❌ Cache MISS - Fetching data for symbol: ${symbolName} (datapackId: ${datapackId})`);

        // Fetch daily data from our existing API
        // Note: API always returns 1D resolution data
        const rawData = await fetchChartData(datapackId, this.sessionId, this.userId);
        logger.info(`[TradingViewDatafeed] Fetched ${rawData.length} data points for ${symbolName}`);

        // Transform to TradingView Bar format
        const bars = this.transformToBars(rawData);
        this.cachedBars.set(datapackId, bars);

        // Cache the earliest and latest bar times for this datapack
        if (bars.length > 0) {
          this.earliestBarTime.set(datapackId, bars[0].time);
          this.latestBarTime.set(datapackId, bars[bars.length - 1].time);
          logger.info(`[TradingViewDatafeed] Data range for ${datapackId}: ${new Date(bars[0].time).toISOString()} to ${new Date(bars[bars.length - 1].time).toISOString()}`);
        }
      }

      const bars = this.cachedBars.get(datapackId) || [];
      const earliestBarTime = this.earliestBarTime.get(datapackId);
      const latestBarTime = this.latestBarTime.get(datapackId);

      // Check if requested range is completely before our earliest data
      // EXCEPTION: On first data request, return all available data to allow TradingView to display historical data
      if (earliestBarTime && periodParams.to * 1000 < earliestBarTime) {
        if (periodParams.firstDataRequest && bars.length > 0) {
          logger.info(`[TradingViewDatafeed] First request outside data range - returning all ${bars.length} bars for initial display`);
          logger.info(`[TradingViewDatafeed] Data range: ${new Date(bars[0].time).toISOString()} to ${new Date(bars[bars.length - 1].time).toISOString()}`);
          onResult(bars, { noData: false });
          return;
        }

        logger.info(`[TradingViewDatafeed] ⚠️  Request range ends before earliest data - returning noData`);
        logger.info(`[TradingViewDatafeed] Request ends: ${new Date(periodParams.to * 1000).toISOString()}, Data starts: ${new Date(earliestBarTime).toISOString()}`);
        onResult([], { noData: true, nextTime: Math.floor(earliestBarTime / 1000) });
        return;
      }

      // Check if requested range is completely after our latest data
      // EXCEPTION: On first data request, return all available data to allow TradingView to display historical data
      if (latestBarTime && periodParams.from * 1000 > latestBarTime) {
        if (periodParams.firstDataRequest && bars.length > 0) {
          logger.info(`[TradingViewDatafeed] First request outside data range - returning all ${bars.length} bars for initial display`);
          logger.info(`[TradingViewDatafeed] Data range: ${new Date(bars[0].time).toISOString()} to ${new Date(bars[bars.length - 1].time).toISOString()}`);
          onResult(bars, { noData: false });
          return;
        }

        logger.info(`[TradingViewDatafeed] ⚠️  Request range starts after latest data - returning noData`);
        onResult([], { noData: true });
        return;
      }

      logger.info(`[TradingViewDatafeed] Time range requested:`, {
        from: new Date(periodParams.from * 1000).toISOString(),
        to: new Date(periodParams.to * 1000).toISOString(),
        firstBarTime: bars.length > 0 ? new Date(bars[0].time).toISOString() : 'none',
        lastBarTime: bars.length > 0 ? new Date(bars[bars.length - 1].time).toISOString() : 'none'
      });

      // Filter bars by requested time range
      // periodParams.from and .to are in Unix seconds, bar.time is in milliseconds
      const filteredBars = bars.filter(bar =>
        bar.time >= periodParams.from * 1000 &&
        bar.time <= periodParams.to * 1000
      );

      logger.info(`[TradingViewDatafeed] Returning ${filteredBars.length} bars for range ${new Date(periodParams.from * 1000).toISOString()} to ${new Date(periodParams.to * 1000).toISOString()}`);

      // Return the filtered bars
      // Note: We don't set noData when filteredBars is empty here because we already
      // checked if the request is outside our data bounds above
      onResult(filteredBars, { noData: false });

    } catch (error) {
      logger.error('[TradingViewDatafeed] Error fetching bars:', error);
      onError(error instanceof Error ? error.message : 'Unknown error fetching data');
    }
  }

  /**
   * Subscribe to real-time updates
   * Placeholder for future implementation - not currently supported
   */
  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    listenerGuid: string,
    _onResetCacheNeededCallback: () => void
  ): void {
    logger.info(`[TradingViewDatafeed] subscribeBars called for ${listenerGuid} - not implemented`);
    // TODO: Implement real-time data streaming when needed
    // This would typically involve:
    // 1. Opening a WebSocket connection
    // 2. Subscribing to updates for this symbol
    // 3. Calling onTick(bar) when new data arrives
  }

  /**
   * Unsubscribe from real-time updates
   * Placeholder for future implementation
   */
  unsubscribeBars(listenerGuid: string): void {
    logger.info(`[TradingViewDatafeed] unsubscribeBars called for ${listenerGuid}`);
    // TODO: Implement when real-time streaming is added
    // This would typically involve:
    // 1. Closing the WebSocket connection
    // 2. Cleaning up any listeners/subscriptions
  }

  /**
   * Transform our API data format to TradingView Bar format
   *
   * Our format: {time: "2025-10-08 00:00:00...", Open, High, Low, Close, Volume}
   * TradingView format: {time: 1728360000000, open, high, low, close, volume}
   */
  private transformToBars(rawData: ChartDataPoint[]): Bar[] {
    return rawData.map(point => ({
      time: this.parseTimeToUnixMs(point.time),
      open: point.Open,
      high: point.High,
      low: point.Low,
      close: point.Close,
      volume: point.Volume || 0,
    }));
  }

  /**
   * Parse timestamp string to Unix milliseconds
   *
   * Input: "2025-10-08 00:00:00.000000000-04:00"
   * Output: 1728360000000 (Unix timestamp in milliseconds)
   *
   * IMPORTANT: TradingView expects milliseconds, not seconds!
   */
  private parseTimeToUnixMs(timeString: string): number {
    const date = new Date(timeString);

    // Validate the parsed date
    if (isNaN(date.getTime())) {
      logger.warn(`[TradingViewDatafeed] Invalid date: ${timeString}`);
      return 0;
    }

    return date.getTime();
  }
}
